import { defineStore } from "pinia";
import { supabase } from "../lib/supabase.js";
import { ref } from "vue";

// A store is a container for the state of a specific part of the application
// It is accessible from any component in the application
export const useUserStore = defineStore("user", () => {
  const user = ref(null);
  const isLoggedIn = ref(false); // State to track if the user both has an account and has confirmed their email
  const error = ref("");
  const loading = ref(false);

  const login = async (email, password) => {
    loading.value = true;
    error.value = "";
    console.log("user.js login function called with email :", email);
    const { data, error: loginError } = await supabase.auth.signInWithPassword({
      email: email,
      password: password,
    });

    if (loginError) {
      error.value = loginError.message;
      loading.value = false;
      return { success: false, error: loginError.message };
    } else {
      user.value = data.user; // Assuming data.user contains the user object
      loading.value = false;
      isLoggedIn.value = data.user && data.user.email_confirmed_at; // Check if the user has confirmed their email
      return { success: true, user: data.user };
    }
  };

  const signup = async (email, password, name) => {
    // Function to sign up a new user
    loading.value = true;
    error.value = "";

    const { data, error: signupError } = await supabase.auth.signUp({
      email: email,
      password: password,
      options: {
        data: {
          name: name,
        },
      },
    });
    if (signupError) {
      error.value = signupError.message;
      loading.value = false;
      return { success: false, error: signupError.message };
    } else {
      user.value = data.user; // Assuming data.user contains the user object
      loading.value = false;
      return { success: true, user: data.user };
    }
  };

  const logout = async () => {
    await supabase.auth.signOut();
    user.value = null;
  };

  // Refresh the local user state from the current Supabase session/user
  const refreshFromSession = async () => {
    loading.value = true;
    error.value = "";
    const { data, error: getUserError } = await supabase.auth.getUser();
    if (getUserError) {
      error.value = getUserError.message;
      user.value = null;
      isLoggedIn.value = false;
      loading.value = false;
      return { success: false, error: getUserError.message };
    }
    const currentUser = data?.user ?? null;
    user.value = currentUser;
    isLoggedIn.value = !!(currentUser && currentUser.email_confirmed_at);
    loading.value = false;
    return { success: true, user: currentUser };
  };

  const setLanguage = async (language) => {
    // Upsert the user's active language into user_preferences
    try {
      // Ensure we have a user id
      let uid = user.value?.id;
      if (!uid) {
        const { data: usr } = await supabase.auth.getUser();
        uid = usr?.user?.id || null;
      }
      if (!uid) {
        console.error("Error setting user language: missing user id");
        return false;
      }

      const { error } = await supabase
        .from("user_preferences")
        .upsert({ user_id: uid, active_language: language });

      if (error) {
        console.error("Error upserting active_language: ", error);
        return false;
      }

      return true;
    } catch (e) {
      console.error("Unexpected error setting user language:", e);
      return false;
    }
  };

  const setNativeLanguage = async (languageCode) => {
    // Upsert the user's native language (language code) into user_preferences
    try {
      // Ensure we have a user id
      let uid = user.value?.id;
      if (!uid) {
        const { data: usr } = await supabase.auth.getUser();
        uid = usr?.user?.id || null;
      }
      if (!uid) {
        console.error("Error setting native language: missing user id");
        return false;
      }

      const { error } = await supabase
        .from("user_preferences")
        .upsert({ user_id: uid, native_language: languageCode });

      if (error) {
        console.error("Error upserting native_language: ", error);
        return false;
      }

      return true;
    } catch (e) {
      console.error("Unexpected error setting native language:", e);
      return false;
    }
  };

  return {
    user,
    error,
    loading,
    signup,
    login,
    logout,
    setLanguage,
    setNativeLanguage,
    refreshFromSession,
  }; // Expose the state and actions to the rest of the app
});
